
import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import NodeDescriptionDialog from './NodeDescriptionDialog';

interface MindMapNodeProps {
  data: {
    label: string;
    color: string;
    description?: string;
    imageUrl?: string;
    isRoot?: boolean;
    boardId?: string;
    onUpdate: (id: string, label: string, color: string, description?: string, imageUrl?: string) => void;
    onDelete: (id: string) => void;
    onAddChild: (parentId: string) => void;
    onConvertToCard: (nodeId: string, columnId: string) => void;
    onEdgeDelete: (edgeId: string) => void;
  };
  id: string;
  selected?: boolean;
}

export default function MindMapNode({ data, id, selected }: MindMapNodeProps) {
  const [showDialog, setShowDialog] = useState(false);

  const handleNodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };

  const handleSave = (label: string, description: string, imageUrl?: string) => {
    data.onUpdate(id, label, data.color, description, imageUrl);
    setShowDialog(false);
  };

  const handleColorChange = (color: string) => {
    data.onUpdate(id, data.label, color, data.description, data.imageUrl);
  };

  const handleDelete = () => {
    setShowDialog(false);
    data.onDelete(id);
  };

  const handleAddChild = () => {
    setShowDialog(false);
    data.onAddChild(id);
  };

  const handleConvertToCard = (columnId: string) => {
    setShowDialog(false);
    data.onConvertToCard(id, columnId);
  };

  return (
    <>
      <div
        className="min-w-[120px] max-w-[200px] rounded-lg shadow-lg border-2 relative group cursor-pointer transition-all duration-200 hover:shadow-xl"
        style={{
          backgroundColor: data.color,
          borderColor: data.color,
          color: '#fff'
        }}
        onClick={handleNodeClick}
      >
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-white border-2"
          style={{ borderColor: data.color }}
        />
        
        <div className="p-3">
          {data.imageUrl && (
            <div className="mb-2">
              <img
                src={data.imageUrl}
                alt={data.label}
                className="w-full h-16 object-cover rounded"
              />
            </div>
          )}
          
          <div className="text-sm font-medium text-center">
            {data.label}
          </div>
          
          {data.description && (
            <div className="text-xs mt-1 opacity-80 text-center line-clamp-2">
              {data.description}
            </div>
          )}
        </div>

        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-white border-2"
          style={{ borderColor: data.color }}
        />

        {/* Hover overlay com ações rápidas */}
        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
          <div className="text-white text-xs">Clique para editar</div>
        </div>
      </div>

      <NodeDescriptionDialog
        open={showDialog}
        onOpenChange={handleDialogClose}
        nodeId={id}
        nodeLabel={data.label}
        nodeDescription={data.description || ''}
        nodeImageUrl={data.imageUrl || ''}
        nodeColor={data.color}
        isRoot={data.isRoot}
        boardId={data.boardId || ''}
        onSave={handleSave}
        onDelete={handleDelete}
        onColorChange={handleColorChange}
        onAddChild={handleAddChild}
        onConvertToCard={handleConvertToCard}
        onEdgeDelete={data.onEdgeDelete}
      />
    </>
  );
}
